import React, { useMemo, useState } from 'react';

import { Col, Segmented, Alert } from 'antd';
import { Formik, Field } from 'formik';

import { getMoment } from '@libs/NwMoment';
import { getTime } from '@libs/NwMoment';

import { useAddFollowupPeriods, forceReloadFollowup } from '@hooks/API/Event/UseFollowup';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import {
    NWLongEventPeriodPicker,
    NWMultipleDateSelector,
    NWRecurrenceSelector,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

import { useFollowUpState, useFollowUpDispatch, ACTIONS } from './FollowUpContext';

const AddDatesToFollowUp = () => {
    const { followUpData, afterUpdate } = useFollowUpState();
    const dispatch = useFollowUpDispatch();
    const { mutateAsync: onAddPeriods } = useAddFollowupPeriods();
    const [errorMessage, setErrorMessage] = useState('');

    // Calculate the next day after the last date of all events (including RelatedEvents)
    const defaultDate = useMemo(() => {
        if (!followUpData) return getMoment().startOf('day');

        const allEndDates = [followUpData.EndDate];
        if (followUpData.RelatedEvents?.length > 0) {
            allEndDates.push(...followUpData.RelatedEvents.map((e) => e.EndDate));
        }
        const lastDate = allEndDates.reduce((max, date) => (date > max ? date : max));
        return getMoment(lastDate).startOf('day').add(1, 'day');
    }, [followUpData]);

    const handleClose = () => {
        dispatch({ type: ACTIONS.SET_ADD_DATES_DRAWER, payload: false });
    };

    const handleUpdateEvent = () => {
        forceReloadFollowup(followUpData.ID);
        if (afterUpdate) {
            afterUpdate();
        }
    };

    const datesModeOptions = [
        { label: 'Period', value: 'period' },
        { label: 'Multiple Dates', value: 'multipleDates' },
        { label: 'Recurrence', value: 'recurrence' },
    ];

    const initialValues = {
        DatesMode: 'period',
        Period: {
            startDate: defaultDate,
            endDate: defaultDate,
            startTime: getTime(defaultDate.clone().hour(9).minute(0)),
            endTime: getTime(defaultDate.clone().hour(9).minute(0)),
            allDay: false,
            dates: [],
        },
        MultipleDates: {
            dates: [defaultDate.toDate()],
            startTime: [0, 0],
            endTime: [23, 59],
            allDay: true,
        },
        Recurrence: {
            startDate: defaultDate,
            endDate: defaultDate.clone().add(6, 'months'),
            every: 1,
            daysOfWeek: [],
            startTime: [0, 0],
            endTime: [23, 59],
            allDay: true,
        },
    };

    const submitForm = async (values, setSubmitting) => {
        let periods = [];
        let recursion = null;

        if (values.DatesMode === 'period') {
            const sd = getMoment(values.Period.startDate)
                .hour(values.Period.startTime[0])
                .minute(values.Period.startTime[1]);
            const ed = getMoment(values.Period.endDate).hour(values.Period.endTime[0]).minute(values.Period.endTime[1]);
            periods = [{ StartDate: sd, EndDate: ed }];
        } else if (values.DatesMode === 'multipleDates') {
            const { dates, startTime, endTime } = values.MultipleDates;
            periods = dates.map((date) => {
                const startDateTime = getMoment(date).hour(startTime[0]).minute(startTime[1]);
                const endDateTime = getMoment(date).hour(endTime[0]).minute(endTime[1]);
                return { StartDate: startDateTime, EndDate: endDateTime };
            });
        } else if (values.DatesMode === 'recurrence') {
            const { startDate, endDate, every, daysOfWeek, startTime, endTime } = values.Recurrence;
            if (daysOfWeek.length === 0) {
                setErrorMessage('Please select at least one day of the week');
                setSubmitting(false);
                return;
            }
            const sd = getMoment(startDate).hour(startTime[0]).minute(startTime[1]);
            const ed = getMoment(endDate).hour(endTime[0]).minute(endTime[1]);
            periods = [{ StartDate: sd, EndDate: ed }];
            recursion = {
                Type: 'Weekly',
                Every: every,
                DaysOfWeek: daysOfWeek,
            };
        }

        if (periods.length === 0) {
            setErrorMessage('No dates selected');
            setSubmitting(false);
            return;
        }

        const periodsData = {
            Periods: periods,
            ...(recursion && { Recursion: recursion }),
        };

        try {
            await onAddPeriods({ followUpID: followUpData.ID, periodsData });
            handleUpdateEvent();
            handleClose();
        } catch (error) {
            console.error('Add FollowUp Periods Error => ', error);
            setErrorMessage('Error adding dates');
        }
        setSubmitting(false);
    };

    return (
        <NwDrawer id="add-dates-drawer" padding="24px" width={730} title="Add Dates" onClose={handleClose}>
            <div className="event-form">
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={false}
                    onSubmit={(values, { setSubmitting }) => {
                        setErrorMessage('');
                        submitForm(values, setSubmitting);
                    }}
                >
                    {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                        <NwForm small values={values} onFinish={handleSubmit} layout="vertical">
                            {/* Dates Mode Selector */}
                            <Col span={24} style={{ marginBottom: '1rem' }}>
                                <Segmented
                                    value={values.DatesMode}
                                    options={datesModeOptions}
                                    onChange={(value) => setFieldValue('DatesMode', value)}
                                />
                            </Col>

                            {/* Period - shown when DatesMode is 'period' */}
                            {values.DatesMode === 'period' && (
                                <Col span={24} style={{ marginBottom: '2rem' }}>
                                    <Field
                                        component={NWLongEventPeriodPicker}
                                        label=""
                                        name="Period"
                                        value={values.Period}
                                    />
                                </Col>
                            )}

                            {/* Multiple Dates - shown when DatesMode is 'multipleDates' */}
                            {values.DatesMode === 'multipleDates' && (
                                <Col span={24}>
                                    <Field
                                        component={NWMultipleDateSelector}
                                        label=""
                                        name="MultipleDates"
                                        value={values.MultipleDates}
                                    />
                                </Col>
                            )}

                            {/* Recurrence - shown when DatesMode is 'recurrence' */}
                            {values.DatesMode === 'recurrence' && (
                                <Col span={24}>
                                    <Field
                                        component={NWRecurrenceSelector}
                                        label=""
                                        name="Recurrence"
                                        value={values.Recurrence}
                                    />
                                </Col>
                            )}

                            <NwFormButtonsBar
                                left={<NwCancelButton disabled={isSubmitting} onClick={handleClose} />}
                                right={
                                    <NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />
                                }
                            />
                            {errorMessage && (
                                <Alert message={errorMessage} type="error" style={{ marginTop: '1rem' }} />
                            )}
                        </NwForm>
                    )}
                </Formik>
            </div>
        </NwDrawer>
    );
};

export default AddDatesToFollowUp;
